// The camera frame of a scanner: the live video fills it, and the shared ScanFrame draws the aiming
// corners and the sweeping line while detection runs. Overlay controls (torch, zoom) go in as children.
import "./config";
import { View } from "tamagui";
import { Column } from "./layout";
import { ScanFrame } from "./ScanFrame";

export interface ViewfinderProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Off-screen frame the detector reads from. */
  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
  /** Detection is running: the sweeping line shows and the overlay is offered. */
  live: boolean;
  minHeight?: number;
  children?: React.ReactNode;
}

export function Viewfinder({
  videoRef,
  canvasRef,
  live,
  minHeight = 260,
  children,
}: ViewfinderProps): React.JSX.Element {
  // Tamagui's props do not list the video attributes.
  const nativeVideo = { playsInline: true, muted: true };
  return (
    <Column
      position="relative"
      minHeight={minHeight}
      borderRadius="4"
      backgroundColor="bg2"
      borderWidth="var(--borderW)"
      borderColor="line"
      overflow="hidden"
      alignItems="center"
      justifyContent="center"
    >
      <View
        render="video"
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        objectFit="cover"
        ref={(node: unknown) => {
          videoRef.current = node instanceof HTMLVideoElement ? node : null;
        }}
        // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
        {...nativeVideo}
      />
      {canvasRef === undefined ? null : (
        <View
          render="canvas"
          display="none"
          ref={(node: unknown) => {
            canvasRef.current = node instanceof HTMLCanvasElement ? node : null;
          }}
        />
      )}
      <ScanFrame sweeping={live} />
      <Column
        position="absolute"
        top={26}
        left={26}
        right={26}
        bottom={26}
        overflow="hidden"
        pointerEvents="none"
      >
        {live ? children : null}
      </Column>
    </Column>
  );
}
